import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { Sparkles, X } from "lucide-react";

const TIER_LABEL = {
  thrifter: "Thrifter",
  reseller: "Reseller",
  pro: "Pro",
};

/**
 * Celebration modal — triggers after a successful pull-based sync grants credits.
 * Fires a confetti burst and displays the total credits added and new plan tier.
 */
export default function PurchaseCelebrationModal({ open, onClose, creditsAdded, tier, balance }) {
  useEffect(() => {
    if (!open) return;
    // Confetti burst — two shots for a fuller feel.
    const shoot = () => {
      confetti({
        particleCount: 90,
        spread: 65,
        startVelocity: 45,
        origin: { y: 0.35 },
        colors: ["#7C3AED", "#10B981", "#F59E0B", "#ffffff", "#6366F1"],
      });
    };
    shoot();
    const t = setTimeout(shoot, 250);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="celebration-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[110] flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-[#09090b] border border-white/10 rounded-3xl p-8 text-center shadow-2xl"
          >
            <button
              data-testid="celebration-close"
              onClick={onClose}
              className="absolute top-4 right-4 text-white/50 hover:text-white text-sm w-7 h-7 rounded-full flex items-center justify-center hover:bg-white/5"
            >
              <X size={16} />
            </button>

            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 12, stiffness: 300, delay: 0.1 }}
              className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center bg-gradient-to-br from-emerald-500 to-emerald-400 shadow-[0_0_40px_-8px_rgba(16,185,129,0.6)]"
            >
              <Sparkles size={28} className="text-black" strokeWidth={2.5} />
            </motion.div>

            <h2 className="font-display text-3xl font-bold mb-2" style={{ color: "#FFFFFF" }} data-testid="celebration-title">
              You&apos;re in.
            </h2>
            {creditsAdded > 0 ? (
              <p className="text-white/70 text-sm mb-5" data-testid="celebration-body">
                <span className="font-mono-num text-emerald-400 font-bold text-lg">
                  {creditsAdded} Credit{creditsAdded === 1 ? "" : "s"}
                </span>{" "}
                added to your account.
              </p>
            ) : (
              <p className="text-white/70 text-sm mb-5" data-testid="celebration-body">
                Your <span className="text-emerald-400 font-semibold">{TIER_LABEL[tier] || "new"} plan</span> is now active.
              </p>
            )}

            <div className="rounded-2xl bg-white/[0.03] border border-white/10 py-3 mb-5">
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-num">Balance</div>
              <div className="font-mono-num text-3xl font-bold text-white">
                {balance ?? "—"} <span className="text-sm text-white/40 font-normal">scans</span>
              </div>
            </div>

            <button
              data-testid="celebration-continue"
              onClick={onClose}
              className="w-full py-3 rounded-full bg-white text-black font-bold text-sm hover:bg-zinc-200 transition-colors"
            >
              Start scanning
            </button>

            <p className="text-[10px] text-zinc-500 mt-4">
              Manage or cancel anytime at{" "}
              <a
                href="https://whop.com/orders"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-300 underline decoration-dotted"
              >
                whop.com/orders
              </a>{" "}
              with the same email you used to purchase.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
